import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const filtered = users.filter((u) => {
    if (role !== "all" && u.role !== role) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.pseudo || "").toLowerCase().includes(q) ||
      (u.merchant_name || "").toLowerCase().includes(q)
    );
  });

  const ROLES = [
    { v: "all", label: "Tous" },
    { v: "habitant", label: "Habitants" },
    { v: "commercant", label: "Commerçants" },
    { v: "admin", label: "Admins" },
  ];

  return (
    <div className="space-y-12">
      <header>
        <div className="phenix-eyebrow mb-3">Annuaire</div>
        <h1 className="heading-display text-2xl lg:text-3xl text-plum-900">
          <em className="text-pink-500">Utilisateurs.</em>
        </h1>
      </header>

      <div className="flex flex-wrap items-center gap-6 border-y border-cream-200 py-5">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} strokeWidth={1.25} className="absolute left-0 top-1/2 -translate-y-1/2 text-plum-700/50" />
          <Input
            className="pl-7 rounded-none border-0 border-b border-cream-200 bg-transparent h-9 focus-visible:ring-0 focus-visible:border-clay-500"
            placeholder="Rechercher..."
            value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="users-search"
          />
        </div>
        <div className="flex gap-8">
          {ROLES.map((r) => (
            <button
              key={r.v}
              onClick={() => setRole(r.v)}
              className={`text-[12px] uppercase tracking-[0.18em] transition-colors ${
                role === r.v ? "text-pink-500 font-medium" : "text-plum-700/70 hover:text-ink-900"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="divide-y divide-cream-200 border-y border-cream-200">
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-plum-700/50 italic">Aucun utilisateur.</div>
        ) : filtered.map((u, i) => (
          <div key={u.id} className="grid lg:grid-cols-12 gap-6 py-6 items-center" data-testid={`user-row-${u.id}`}>
            <div className="lg:col-span-1 font-mono text-[11px] text-plum-700/50">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="lg:col-span-4">
              <div className="font-display text-lg text-ink-900">{u.name}</div>
              <div className="text-[11px] text-plum-700/50">{u.email}</div>
            </div>
            <div className="lg:col-span-3">
              <div className="font-mono text-sm text-plum-900">{u.pseudo}</div>
              {u.pseudo_status === "pending" && (
                <div className="text-[10px] uppercase tracking-widest text-pink-500 mt-0.5">À modérer</div>
              )}
              {u.pseudo_status === "rejected" && (
                <div className="text-[10px] uppercase tracking-widest text-plum-700/50 italic mt-0.5">Rejeté</div>
              )}
            </div>
            <div className="lg:col-span-2">
              <span className="text-[11px] uppercase tracking-widest text-plum-700">{u.role}</span>
              {u.merchant_name && (
                <div className="text-[11px] text-plum-700/50 italic mt-0.5">{u.merchant_name}</div>
              )}
            </div>
            <div className="lg:col-span-2 text-right">
              <div className="font-display text-2xl text-ink-900 tabular-nums">
                {u.points}<span className="text-pink-500 text-sm font-light"> pts</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
